/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印提取逻辑 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-09 20:00:00
 */

import { useCallback } from 'react'
import { message } from 'antd'
import { useWatermarkExtraction } from '../../hooks/useWatermarkExtraction'
import {
  WatermarkExtractionStatus,
  PhotoItem,
  WatermarkItem
} from '../../context/WatermarkBatchContext'
import { TestDataItem } from './useCSVUpload'

interface UseWatermarkExtractParams {
  dataSource: TestDataItem[]
  currentPage: number
  pageSize: number
  updateDataSource: (updater: (prev: TestDataItem[]) => TestDataItem[]) => void
  setExtracting: (extracting: boolean) => void
  setHasCompletedExtraction: (completed: boolean) => void
}

export const useWatermarkExtract = ({
  dataSource,
  currentPage,
  pageSize,
  updateDataSource,
  setExtracting,
  setHasCompletedExtraction
}: UseWatermarkExtractParams) => {
  const { extractWatermarks } = useWatermarkExtraction()

  const handleExtract = useCallback(async () => {
    if (dataSource.length === 0) {
      message.warning('请先上传测试数据')
      return
    }

    // 计算当前页的数据范围
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    const currentPageData = dataSource.slice(startIndex, endIndex)

    if (currentPageData.length === 0) {
      message.warning('当前页没有数据')
      return
    }

    // 过滤掉已有 autoUrl 的项（已有自动处理链接的跳过提取）
    const itemsToExtract = currentPageData.filter((item) => !item.autoUrl)

    if (itemsToExtract.length === 0) {
      message.info('当前页所有图片都已自动处理，无需提取')
      return
    }

    // 将 TestDataItem 转换为 PhotoItem
    // 处理 URL 格式（去除反引号和空格，确保与接口返回格式一致）
    const photos: PhotoItem[] = itemsToExtract.map((item, index) => ({
      seq: startIndex + index + 1,
      url: item.oldUrl,
      aiURL: '',
      smallURL: item.oldUrl
        ? item.oldUrl.replace(/`/g, '').trim()
        : item.oldUrl,
      watermarkStatus: WatermarkExtractionStatus.EXTRACTING
    }))

    setExtracting(true)

    // 更新当前页数据的状态为提取中（只更新需要提取的项）
    updateDataSource((prev) => {
      const updated = [...prev]
      itemsToExtract.forEach((item) => {
        const index = updated.findIndex((d) => d.key === item.key)
        if (index !== -1) {
          updated[index] = {
            ...updated[index],
            extractionStatus: WatermarkExtractionStatus.EXTRACTING
          }
        }
      })
      return updated
    })

    try {
      await extractWatermarks(
        photos,
        // 照片状态更新回调
        (updatedPhotos: PhotoItem[], status: WatermarkExtractionStatus) => {
          console.log('updatedPhotos====', updatedPhotos, status)
          updateDataSource((prev) => {
            const updated = [...prev]
            updatedPhotos.forEach((photo) => {
              // 处理 URL 格式（去除反引号和空格，与接口返回格式保持一致）
              const photoUrl = photo.smallURL
                ? photo.smallURL.replace(/`/g, '').trim()
                : ''

              // 通过 oldUrl 匹配数据（因为创建 PhotoItem 时使用的是 oldUrl）
              const index = updated.findIndex((d) => {
                const dataUrl = d.oldUrl
                  ? d.oldUrl.replace(/`/g, '').trim()
                  : ''
                return dataUrl === photoUrl
              })

              if (index !== -1) {
                console.log('匹配到数据，更新状态:', {
                  index,
                  oldUrl: updated[index].oldUrl,
                  photoUrl,
                  status
                })
                updated[index] = {
                  ...updated[index],
                  extractionStatus: status,
                  taskID: photo.taskID,
                  errorMsg: photo.errorMsg
                }
              } else {
                console.warn('未匹配到数据:', {
                  photoUrl,
                  oldUrls: updated.map((d) => d.oldUrl)
                })
              }
            })
            return updated
          })
        },
        // 水印数据更新回调（保存水印数据）
        (
          photoUrl: string,
          watermarkID: string,
          watermarkData: WatermarkItem[]
        ) => {
          updateDataSource((prev) => {
            const updated = [...prev]
            const index = updated.findIndex((d) => {
              const dataUrl = d.oldUrl ? d.oldUrl.replace(/`/g, '').trim() : ''
              const url = photoUrl ? photoUrl.replace(/`/g, '').trim() : ''
              return dataUrl === url
            })
            if (index !== -1) {
              updated[index] = {
                ...updated[index],
                watermarkData,
                watermarkID
              }
            }
            return updated
          })
        },
        // 提取完成回调
        () => {
          setExtracting(false)
          // 设置提取完成状态（基于操作成功）
          setHasCompletedExtraction(true)
          message.success('提取完成，可以进行第二步：应用水印')
        }
      )
    } catch (error) {
      setExtracting(false)
      console.error('提取失败:', error)
      // 更新失败状态
      updateDataSource((prev) => {
        const updated = [...prev]
        currentPageData.forEach((item) => {
          const index = updated.findIndex((d) => d.key === item.key)
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              extractionStatus: WatermarkExtractionStatus.FAILED
            }
          }
        })
        return updated
      })
    }
  }, [
    dataSource,
    currentPage,
    pageSize,
    extractWatermarks,
    updateDataSource,
    setExtracting,
    setHasCompletedExtraction
  ])

  return {
    handleExtract
  }
}
